// Replace only one order, preserving references to all unaffected cards.
export function installationDates(installation) {
  if (!installation?.planned_start_date) return [];
  if (installation.planned_dates?.length) return [...installation.planned_dates].sort();
  const days = [];
  const cursor = new Date(`${installation.planned_start_date}T12:00:00Z`);
  const end = new Date(`${installation.planned_end_date || installation.planned_start_date}T12:00:00Z`);
  while (cursor <= end && days.length <= 366) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

export function shiftInstallationDates(installation, targetDay) {
  const days = installationDates(installation);
  if (!days.length) return [targetDay];
  const offset = new Date(`${targetDay}T12:00:00Z`) - new Date(`${days[0]}T12:00:00Z`);
  return days.map(day => new Date(new Date(`${day}T12:00:00Z`).getTime() + offset).toISOString().slice(0, 10));
}

export function replaceInstallation(current, installation, include) {
  if (!include(installation)) return current.filter(item => item.id !== installation.id);
  if (!current.some(item => item.id === installation.id)) return [...current, installation];
  return current.map(item => item.id === installation.id ? installation : item);
}

// A read started before a mutation must not resurrect that order's old state.
export function mergeCalendarRead(remote, current, protectedIds) {
  return [
    ...remote.filter(item => !protectedIds.has(item.id)),
    ...current.filter(item => protectedIds.has(item.id)),
  ];
}

export function optimisticSchedule(installation, payload, teams, automatic = {}) {
  if (payload.action === 'card_add' || payload.action === 'card_move') {
    // Keep the authoritative plan visible until the scoped transaction completes.
    return { ...installation, _schedulePending: true };
  }
  const scheduling = payload.action === 'schedule';
  const scheduledDays = installationDates(payload);
  const day = scheduling ? payload.assigned_date || payload.planned_start_date : payload.assigned_date;
  let assignments = [...(installation.assignments || [])];
  if (payload.replace_regular_assignments) {
    const specialIds = new Set(teams.filter(team => (
      team.is_special_truck || team.is_special_dad
    )).map(team => team.id));
    assignments = assignments.filter(item => specialIds.has(item.team_id));
  }
  if (scheduling) {
    const specialTeams = teams.filter(team => (
      (automatic.tir && team.is_special_truck) || (automatic.dad && team.is_special_dad)
    ));
    const specialIds = new Set(specialTeams.map(team => team.id));
    assignments = assignments.filter(item => !specialIds.has(item.team_id)
      || scheduledDays.includes(item.assigned_date));
    if (payload.planned_dates) assignments = assignments.filter(item => scheduledDays.includes(item.assigned_date));
    const cursor = new Date(`${payload.planned_start_date}T12:00:00Z`);
    const end = new Date(`${payload.planned_end_date}T12:00:00Z`);
    const addSpecialAssignments = assignedDate => {
      specialTeams.forEach(team => {
        if (team.is_special_truck && !automatic.mandatoryTir
          && installation.delivery_team_excluded_dates?.includes(assignedDate)) return;
        const hasRegularTeam = assignments.some(item => item.assigned_date === assignedDate
          && teams.some(crew => crew.id === item.team_id && !crew.is_special_truck && !crew.is_special_dad));
        if (team.is_special_dad && hasRegularTeam) return;
        if (!assignments.some(item => item.team_id === team.id && item.assigned_date === assignedDate)) {
          assignments.push({ id: `pending-${installation.id}-${team.id}-${assignedDate}`,
            installation_id: installation.id, team_id: team.id, team_name: team.name,
            installer_id: null, assigned_date: assignedDate, display_order: 0 });
        }
      });
    };
    while (cursor <= end) {
      if (scheduledDays.includes(cursor.toISOString().slice(0, 10))) addSpecialAssignments(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
  }
  if (payload.action === 'move') {
    assignments = assignments.filter(item => !(
      item.team_id === payload.source_team_id && item.assigned_date === day
    ));
  }
  if (payload.team_id != null) {
    const team = teams.find(item => item.id === payload.team_id);
    if (installation.order_number?.toUpperCase().startsWith('DAD/') && !team?.is_special_dad) {
      const dadId = teams.find(item => item.is_special_dad)?.id;
      assignments = assignments.filter(item => !(item.team_id === dadId && item.assigned_date === day));
    }
    if (!assignments.some(item => item.team_id === payload.team_id && item.assigned_date === day)) {
      assignments.push({
        id: `pending-${installation.id}`, installation_id: installation.id,
        team_id: payload.team_id, team_name: team?.name,
        installer_id: null, assigned_date: day, display_order: Number.MAX_SAFE_INTEGER,
      });
    }
  }
  return {
    ...installation,
    ...(scheduling ? {
      planned_start_date: payload.planned_start_date,
      planned_end_date: payload.planned_end_date,
      planned_dates: payload.planned_dates || null,
    } : {}),
    assignments,
    _schedulePending: true,
  };
}
